import { z } from "zod";
import { bookGenerationCostEvidenceSourceSchema, bookGenerationPromptBindingV1Schema, bookGenerationProviderSchema, bookGenerationRetryLineageV1Schema, bookGenerationSourceBindingV1Schema, bookGenerationUsageV1Schema, } from "./book-generation.js";
import { bookCreationProfileForTemplate } from "./creation-profile.js";
import { familyCreativeGenerationBindingSchema } from "./family-creative-session.js";
import { bookProductionProductSchema } from "./book-production-evidence.js";
import { bookRoleSchema, ulidSchema } from "./schemas.js";
export const studioVersionV1Schema = z.literal(1);
export const contentModelSchema = z.enum(["NARRATIVE", "PICTURE_BOOK", "RECIPE", "COMIC_CORE"]);
export const studioPhaseSchema = z.enum(["plan", "gather", "build", "design", "review", "finish"]);
export const studioTaskStateSchema = z.enum([
    "NOT_STARTED",
    "IN_PROGRESS",
    "READY",
    "COMPLETE",
    "BLOCKED",
    "NEEDS_ATTENTION",
]);
export const studioTaskStatusSchema = z
    .object({
    id: z.string().min(1).max(120),
    label: z.string().min(1).max(160),
    phase: studioPhaseSchema,
    itemId: z.string().min(1).max(160).optional(),
    state: studioTaskStateSchema,
    required: z.boolean(),
    completedAt: z.string().datetime().optional(),
    blockerIds: z.array(z.string().min(1).max(120)).max(20).default([]),
})
    .strict();
export const studioCapabilitySchema = z.enum([
    "VOICE_CAPTURE",
    "SOURCE_EVIDENCE",
    "CHAPTER_PHOTOS",
    "STRUCTURED_RECIPES",
    "STORY_BEATS",
    "PAGE_SPREADS",
    "CHARACTER_REFERENCES",
    "AI_ILLUSTRATION",
    "NARRATION",
    "SOUNDSCAPE",
    "PRINT_QR",
    "COMIC_CORE",
]);
export const templateStudioProfileSchema = z
    .object({
    studioVersion: studioVersionV1Schema,
    templateId: z.string().regex(/^[a-z][a-z0-9-]+$/),
    contentModel: contentModelSchema,
    itemLabel: z.string().min(1).max(40),
    itemLabelPlural: z.string().min(1).max(40),
    capabilities: z.array(studioCapabilitySchema),
    requiredApprovals: z.array(z.string().min(1).max(80)).max(12),
    generationStages: z.array(z.string().min(1).max(100)).min(1).max(12),
})
    .strict();
const memoirCapabilities = [
    "VOICE_CAPTURE",
    "SOURCE_EVIDENCE",
    "CHAPTER_PHOTOS",
    "PRINT_QR",
];
export function studioProfileForTemplate(templateId) {
    const creationProfile = bookCreationProfileForTemplate(templateId);
    if (!creationProfile || creationProfile.registration !== "CURRENT_CATALOG")
        throw new Error(`Unknown current catalog template: ${templateId || "(empty)"}.`);
    if (creationProfile.productionFamily === "PICTURE_BOOK")
        return {
            studioVersion: 1,
            templateId,
            contentModel: "PICTURE_BOOK",
            itemLabel: creationProfile.itemLabel,
            itemLabelPlural: creationProfile.itemLabelPlural,
            capabilities: [
                "VOICE_CAPTURE",
                "STORY_BEATS",
                "PAGE_SPREADS",
                "CHARACTER_REFERENCES",
                "AI_ILLUSTRATION",
                "NARRATION",
                "SOUNDSCAPE",
            ],
            requiredApprovals: ["MANUSCRIPT", "VISUAL_SYSTEM", "FINAL_PROOF"],
            generationStages: [
                "SOURCE_MEMORY",
                "MANUSCRIPT_CANDIDATES",
                "EDITORIAL_CRITIQUE",
                "HUMAN_MANUSCRIPT_APPROVAL",
                "EIGHT_TURN_STORYBOARD",
                "CONTINUITY_ANCHOR_AND_ILLUSTRATIONS",
                "SEQUENCE_QUALITY_REVIEW",
                "HUMAN_FINAL_PROOF",
                "READER_AND_PDF",
            ],
        };
    if (creationProfile.productionFamily === "RECIPE")
        return {
            studioVersion: 1,
            templateId,
            contentModel: "RECIPE",
            itemLabel: creationProfile.itemLabel,
            itemLabelPlural: creationProfile.itemLabelPlural,
            capabilities: ["VOICE_CAPTURE", "STRUCTURED_RECIPES", "CHAPTER_PHOTOS"],
            requiredApprovals: ["RECIPE_STRUCTURE", "FINAL_PROOF"],
            generationStages: [
                "SOURCE_RECIPE_MEMORY",
                "STRUCTURED_RECIPE_CANDIDATE",
                "CULINARY_ACCURACY_REVIEW",
                "HUMAN_QUANTITY_AND_STEP_APPROVAL",
                "ORDERED_COOKBOOK_PREVIEW",
                "FINAL_PDF",
            ],
        };
    if (creationProfile.productionFamily === "COMIC_CORE")
        return {
            studioVersion: 1,
            templateId,
            contentModel: "COMIC_CORE",
            itemLabel: creationProfile.itemLabel,
            itemLabelPlural: creationProfile.itemLabelPlural,
            capabilities: [...memoirCapabilities, "COMIC_CORE"],
            requiredApprovals: ["MANUSCRIPT", "FINAL_PROOF"],
            generationStages: [
                "SOURCE_MEMORY",
                "ISSUE_OUTLINE",
                "PAGE_AND_PANEL_SCRIPT",
                "DIALOGUE_AND_CONTINUITY_REVIEW",
                "HUMAN_SCRIPT_APPROVAL",
                "COMPLETE_PREVIEW",
                "FINAL_PDF",
            ],
        };
    if (creationProfile.productionFamily === "NARRATIVE")
        return {
            studioVersion: 1,
            templateId,
            contentModel: "NARRATIVE",
            itemLabel: creationProfile.itemLabel,
            itemLabelPlural: creationProfile.itemLabelPlural,
            capabilities: [...memoirCapabilities],
            requiredApprovals: ["MANUSCRIPT", "FINAL_PROOF"],
            generationStages: [
                "SOURCE_MEMORIES",
                "WHOLE_BOOK_OUTLINE",
                "CHAPTER_DRAFTS",
                "EVIDENCE_AND_QUALITY_REVIEW",
                "HUMAN_CHAPTER_APPROVALS",
                "COMPLETE_BOOK_PREVIEW",
                "FINAL_PDF",
            ],
        };
    return creationProfile.productionFamily;
}
export const studioBlockerSchema = z
    .object({
    id: z.string().min(1).max(120),
    phase: studioPhaseSchema,
    itemId: z.string().min(1).max(160).optional(),
    title: z.string().min(1).max(160),
    detail: z.string().min(1).max(1_000),
    resolutionPath: z.string().startsWith("/app/"),
})
    .strict();
export const studioJobSchema = z
    .object({
    id: z.string().min(1).max(160),
    kind: z.enum(["GENERATION", "TRANSCRIPTION", "PHOTO_PROCESSING", "RENDERING"]),
    label: z.string().min(1).max(160),
    state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "NEEDS_ATTENTION"]),
    progress: z.number().int().min(0).max(100).optional(),
    retryPath: z.string().startsWith("/app/").optional(),
})
    .strict();
export const studioPhaseSummarySchema = z
    .object({
    id: studioPhaseSchema,
    label: z.string().min(1).max(80),
    state: studioTaskStateSchema,
    completedRequired: z.number().int().nonnegative(),
    totalRequired: z.number().int().nonnegative(),
    tasks: z.array(studioTaskStatusSchema),
})
    .strict();
export const studioSnapshotSchema = z
    .object({
    studioVersion: studioVersionV1Schema,
    book: z
        .object({
        id: ulidSchema,
        title: z.string().min(1).max(160),
        storytellerName: z.string().min(1).max(100),
        revision: z.number().int().nonnegative(),
        state: z.string().min(1).max(80),
        role: bookRoleSchema,
        templateName: z.string().min(1).max(160),
    })
        .strict(),
    profile: templateStudioProfileSchema,
    phases: z.array(studioPhaseSummarySchema).length(6),
    blockers: z.array(studioBlockerSchema),
    jobs: z.array(studioJobSchema),
    permissions: z
        .object({
        edit: z.boolean(),
        approve: z.boolean(),
        managePhotos: z.boolean(),
        finish: z.boolean(),
    })
        .strict(),
    progress: z.number().int().min(0).max(100),
    nextAction: z
        .object({
        label: z.string().min(1).max(160),
        phase: studioPhaseSchema,
        itemId: z.string().min(1).max(160).optional(),
        path: z.string().startsWith("/app/"),
    })
        .strict(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const photoProcessingStateSchema = z.enum([
    "UPLOADING",
    "QUARANTINED",
    "PROCESSING",
    "READY",
    "NEEDS_ATTENTION",
    "REJECTED",
    "TRASHED",
    "ARCHIVED",
    "DELETED",
]);
export const photoPrintQualitySchema = z.enum(["UNKNOWN", "GOOD", "CAUTION", "LOW"]);
export const photoOrientationSchema = z.enum(["LANDSCAPE", "PORTRAIT", "SQUARE", "UNKNOWN"]);
export const photoAssetV1Schema = z
    .object({
    version: z.literal(1),
    id: ulidSchema,
    ownerId: ulidSchema,
    filename: z.string().min(1).max(255),
    displayTitle: z.string().max(160).default(""),
    caption: z.string().max(2_000).default(""),
    altText: z.string().max(1_000).default(""),
    decorative: z.boolean().default(false),
    tags: z.array(z.string().min(1).max(60)).max(50).default([]),
    people: z.array(z.string().min(1).max(100)).max(50).default([]),
    events: z.array(z.string().min(1).max(100)).max(50).default([]),
    favorite: z.boolean().default(false),
    captureDate: z.string().date().optional(),
    uploadedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    contentType: z.enum(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]),
    byteSize: z
        .number()
        .int()
        .nonnegative()
        .max(25 * 1024 * 1024),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    orientation: photoOrientationSchema.default("UNKNOWN"),
    processingState: photoProcessingStateSchema,
    printQuality: photoPrintQualitySchema.default("UNKNOWN"),
    thumbnailUrl: z.string().url().optional(),
    previewUrl: z.string().url().optional(),
    trashedAt: z.string().datetime().optional(),
    purgeAfter: z.string().datetime().optional(),
})
    .strict();
export const photoBookAssociationSchema = z
    .object({
    version: z.literal(1),
    photoId: ulidSchema,
    bookId: ulidSchema,
    ownerId: ulidSchema,
    attachedBy: ulidSchema,
    attachedAt: z.string().datetime(),
})
    .strict();
/** The deliberately narrow photo projection that may be shown to another member of a book. */
export const bookPhotoV1Schema = z
    .object({
    version: z.literal(1),
    id: ulidSchema,
    displayTitle: z.string().max(160).default(""),
    caption: z.string().max(2_000).default(""),
    altText: z.string().max(1_000).default(""),
    decorative: z.boolean().default(false),
    contentType: photoAssetV1Schema.shape.contentType,
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    orientation: photoOrientationSchema.default("UNKNOWN"),
    processingState: photoProcessingStateSchema,
    printQuality: photoPrintQualitySchema.default("UNKNOWN"),
    thumbnailUrl: z.string().url().optional(),
    previewUrl: z.string().url().optional(),
    usageCount: z.number().int().nonnegative().default(0),
})
    .strict();
export const photoUsagePurposeSchema = z.enum([
    "PRINT",
    "COVER",
    "CHAPTER",
    "RECIPE",
    "MEMORY_REFERENCE",
    "LIKENESS_REFERENCE",
    "QR_MEDIA",
]);
export const photoPlacementRoleSchema = z.enum([
    "COVER",
    "BACK_COVER",
    "CHAPTER_OPENING",
    "CHAPTER_INLINE",
    "RECIPE_HERO",
    "RECIPE_STEP",
    "FAMILY_STORY",
    "MEMORY_REFERENCE",
    "CHARACTER_LIKENESS",
    "LAYOUT_SLOT",
    "QR_CHAPTER_MEDIA",
]);
const placementPurposeByRole = {
    COVER: "COVER",
    BACK_COVER: "COVER",
    CHAPTER_OPENING: "CHAPTER",
    CHAPTER_INLINE: "CHAPTER",
    RECIPE_HERO: "RECIPE",
    RECIPE_STEP: "RECIPE",
    FAMILY_STORY: "RECIPE",
    MEMORY_REFERENCE: "MEMORY_REFERENCE",
    CHARACTER_LIKENESS: "LIKENESS_REFERENCE",
    LAYOUT_SLOT: "PRINT",
    QR_CHAPTER_MEDIA: "QR_MEDIA",
};
function validatePlacementPurpose(placement, context) {
    const expectedPurpose = placementPurposeByRole[placement.role];
    if (placement.purpose !== expectedPurpose)
        context.addIssue({
            code: "custom",
            path: ["purpose"],
            message: `${placement.role} placements require the ${expectedPurpose} purpose.`,
        });
}
export const photoPlacementV1Schema = z
    .object({
    version: z.literal(1),
    id: ulidSchema,
    photoId: ulidSchema,
    bookId: ulidSchema,
    contentItemId: z.string().min(1).max(160).optional(),
    role: photoPlacementRoleSchema,
    purpose: photoUsagePurposeSchema,
    crop: z
        .object({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        width: z.number().positive().max(1),
        height: z.number().positive().max(1),
    })
        .strict(),
    focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict(),
    captionOverride: z.string().max(2_000).optional(),
    altTextOverride: z.string().max(1_000).optional(),
    layoutSlot: z.string().min(1).max(80).optional(),
    placedWidthInches: z.number().positive().max(24).default(6),
    placedHeightInches: z.number().positive().max(24).default(4),
    effectiveDpi: z.number().int().nonnegative().default(0),
    placementPrintQuality: photoPrintQualitySchema.default("UNKNOWN"),
    consentReceiptId: ulidSchema.optional(),
    revision: z.number().int().positive(),
    createdBy: ulidSchema,
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((placement, context) => {
    validatePlacementPurpose(placement, context);
    if (placement.purpose === "LIKENESS_REFERENCE" && !placement.consentReceiptId)
        context.addIssue({
            code: "custom",
            path: ["consentReceiptId"],
            message: "Likeness references require purpose-specific consent.",
        });
});
export const photoUploadItemSchema = z
    .object({
    filename: z.string().min(1).max(255),
    contentType: photoAssetV1Schema.shape.contentType,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024),
    checksumSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/i)
        .optional(),
})
    .strict();
export const createPhotoUploadsCommandSchema = z
    .object({
    files: z.array(photoUploadItemSchema).min(1).max(50),
    bookId: ulidSchema.optional(),
})
    .strict();
export const updatePhotoMetadataCommandSchema = z
    .object({
    displayTitle: z.string().max(160).optional(),
    caption: z.string().max(2_000).optional(),
    altText: z.string().max(1_000).optional(),
    decorative: z.boolean().optional(),
    captureDate: z.string().date().nullable().optional(),
    tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
    people: z.array(z.string().trim().min(1).max(100)).max(50).optional(),
    events: z.array(z.string().trim().min(1).max(100)).max(50).optional(),
    favorite: z.boolean().optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, "Provide at least one metadata change.");
export const createPhotoPlacementCommandSchema = photoPlacementV1Schema
    .omit({
    version: true,
    id: true,
    bookId: true,
    createdBy: true,
    createdAt: true,
    revision: true,
    effectiveDpi: true,
    placementPrintQuality: true,
})
    .extend({ expectedBookRevision: z.number().int().nonnegative() })
    .strict()
    .superRefine((placement, context) => {
    validatePlacementPurpose(placement, context);
    if (placement.purpose === "LIKENESS_REFERENCE" && !placement.consentReceiptId)
        context.addIssue({
            code: "custom",
            path: ["consentReceiptId"],
            message: "Likeness references require purpose-specific consent.",
        });
});
export const updatePhotoPlacementCommandSchema = createPhotoPlacementCommandSchema
    .omit({ photoId: true })
    .extend({ expectedPlacementRevision: z.number().int().positive() })
    .strict();
export const deletePhotoPlacementCommandSchema = z
    .object({
    expectedBookRevision: z.number().int().nonnegative(),
    expectedPlacementRevision: z.number().int().positive(),
})
    .strict();
export const createPhotoConsentCommandSchema = z
    .object({
    subjectKind: z.enum(["ADULT_SELF_OR_AUTHORIZED", "CHILD_GUARDIAN_ATTESTATION"]),
    purpose: z.literal("LIKENESS_REFERENCE"),
    attested: z.literal(true),
})
    .strict();
export const photoUsageSchema = z
    .object({
    placementId: ulidSchema,
    bookId: ulidSchema,
    bookTitle: z.string().min(1).max(160),
    contentItemId: z.string().min(1).max(160).optional(),
    contentItemTitle: z.string().min(1).max(200).optional(),
    role: photoPlacementRoleSchema,
    purpose: photoUsagePurposeSchema,
    resolutionPath: z.string().startsWith("/app/"),
    immutableHistory: z.boolean().default(false),
})
    .strict();
export const recipeIngredientSchema = z
    .object({
    id: z.string().min(1).max(80),
    quantity: z.string().min(1).max(40),
    unit: z.string().max(40),
    ingredient: z.string().min(1).max(240),
    preparation: z.string().max(240).default(""),
    approved: z.boolean(),
})
    .strict();
export const recipeContentV1Schema = z
    .object({
    version: z.literal(1),
    id: z.string().min(1).max(160),
    title: z.string().min(1).max(200),
    familyStory: z.string().max(20_000),
    yield: z.string().max(100),
    preparationMinutes: z.number().int().nonnegative().max(10_000).nullable(),
    cookingMinutes: z.number().int().nonnegative().max(10_000).nullable(),
    ingredientGroups: z
        .array(z
        .object({
        id: z.string().min(1).max(80),
        title: z.string().max(120),
        ingredients: z.array(recipeIngredientSchema).min(1).max(100),
    })
        .strict())
        .max(20),
    instructions: z
        .array(z
        .object({
        id: z.string().min(1).max(80),
        text: z.string().min(1).max(2_000),
        approved: z.boolean(),
    })
        .strict())
        .max(100),
    notes: z.string().max(10_000),
    variations: z.string().max(10_000),
    attribution: z.string().max(500),
    dietaryNotes: z.array(z.string().min(1).max(100)).max(30),
    legacyNotes: z.string().max(100_000).optional(),
    needsStructureReview: z.boolean(),
    heroPhotoPlacementId: ulidSchema.optional(),
    stepPhotoPlacementIds: z.array(ulidSchema).max(100),
    sourceAssetIds: z.array(ulidSchema).max(100).default([]),
    /** A video of someone making the recipe, printed as a code on its page (owner decision D5). */
    videoUrl: z
        .string()
        .trim()
        .url()
        .max(500)
        .refine((value) => /^https:\/\//u.test(value), "Use a secure (https) link.")
        .optional(),
    revision: z.number().int().nonnegative(),
})
    .strict();
/** A private dictation proposal is not a recipe, culinary approval, or provider-cost receipt. */
export const recipeTranscriptProposalSchema = z
    .object({
    jobId: ulidSchema,
    recipeId: z.string().min(1).max(160),
    assetId: ulidSchema,
    sourceRecipeRevision: z.number().int().positive(),
    state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "NEEDS_ATTENTION"]),
    transcript: z.string().min(1).max(100_000).optional(),
    confirmedRevision: z.number().int().positive().optional(),
    createdAt: z.string().datetime(),
})
    .strict();
export const confirmRecipeTranscriptCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    /** Full customer-reviewed draft: concurrent local typing must not be discarded. */
    recipe: recipeContentV1Schema.omit({ revision: true }),
    reviewed: z.literal(true),
})
    .strict();
export const studioContentProductionActionSchema = z.enum([
    "NARRATIVE_BOOK_PLAN",
    "NARRATIVE_BOOK_OUTLINE",
    "NARRATIVE_OUTLINE",
    "NARRATIVE_DRAFT",
    "NARRATIVE_CRITIQUE",
    "NARRATIVE_FINAL",
    "RECIPE_PARSE",
    "RECIPE_CRITIQUE",
    "RECIPE_FINAL",
    "COMIC_OUTLINE",
    "COMIC_SCRIPT",
    "COMIC_CRITIQUE",
    "COMIC_FINAL",
]);
export const studioCreationBriefGenerationBindingV1Schema = z
    .object({
    version: z.literal(1),
    briefId: z.string().trim().min(1).max(240),
    briefRevision: z.number().int().positive(),
    product: bookProductionProductSchema,
    bookRevision: z.number().int().nonnegative(),
    briefDigestSha256: z.string().regex(/^[a-f0-9]{64}$/),
})
    .strict();
export const studioContentProductionArtifactV1Schema = z
    .object({
    version: z.literal(1),
    id: ulidSchema,
    bookId: ulidSchema,
    contentItemId: z.string().min(1).max(160),
    sourceRevision: z.number().int().nonnegative(),
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/),
    contentModel: z.enum(["NARRATIVE", "RECIPE", "COMIC_CORE"]),
    action: studioContentProductionActionSchema,
    result: z.record(z.string(), z.unknown()),
    modelId: z.string().min(1).max(160),
    providerRequestId: z.string().min(1).max(500),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
})
    .strict();
export const studioContentProductionArtifactV2Schema = z
    .object({
    version: z.literal(2),
    id: ulidSchema,
    bookId: ulidSchema,
    contentItemId: z.string().min(1).max(160),
    sourceRevision: z.number().int().nonnegative(),
    sourceHash: z.string().regex(/^[a-f0-9]{64}$/),
    contentModel: z.enum(["NARRATIVE", "RECIPE", "COMIC_CORE"]),
    action: studioContentProductionActionSchema,
    result: z.record(z.string(), z.unknown()),
    modelId: z.string().min(1).max(500),
    provider: bookGenerationProviderSchema,
    providerRequestId: z.string().min(1).max(500),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    actualCostMicros: z.number().int().nonnegative(),
    costEvidenceSource: bookGenerationCostEvidenceSourceSchema,
    pricingVersion: z.string().min(1).max(100),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    creativeSessionBindings: z.array(familyCreativeGenerationBindingSchema).max(100).optional(),
    creationBriefBinding: studioCreationBriefGenerationBindingV1Schema.optional(),
    promptBinding: bookGenerationPromptBindingV1Schema,
    usage: bookGenerationUsageV1Schema,
    retryLineage: bookGenerationRetryLineageV1Schema,
    budgetReservationId: ulidSchema,
    createdAt: z.string().datetime(),
})
    .strict();
export const studioContentProductionArtifactSchema = z.discriminatedUnion("version", [
    studioContentProductionArtifactV1Schema,
    studioContentProductionArtifactV2Schema,
]);
export const studioContentProductionV1Schema = z
    .object({
    version: z.literal(1),
    contentItemId: z.string().min(1).max(160),
    contentModel: z.enum(["NARRATIVE", "RECIPE", "COMIC_CORE"]),
    status: z.enum(["NOT_STARTED", "GENERATING", "READY", "NEEDS_ATTENTION", "COMPLETE"]),
    progressPercent: z.number().int().min(0).max(100),
    next: z
        .object({
        action: studioContentProductionActionSchema,
        label: z.string().min(1).max(160),
    })
        .strict()
        .optional(),
    blocker: z.string().min(1).max(1_000).optional(),
    artifacts: z.array(studioContentProductionArtifactSchema).max(100),
    jobs: z
        .array(z
        .object({
        id: ulidSchema,
        action: studioContentProductionActionSchema,
        sourceRevision: z.number().int().nonnegative(),
        sourceHash: z.string().regex(/^[a-f0-9]{64}$/),
        state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "NEEDS_ATTENTION"]),
        failureReason: z.string().max(1_000).optional(),
        rootJobId: ulidSchema.optional(),
        retryOfJobId: ulidSchema.optional(),
        attempt: z.number().int().nonnegative().max(3).optional(),
        budgetReservationId: ulidSchema.optional(),
        provider: bookGenerationProviderSchema.optional(),
        modelId: z.string().min(1).max(500).optional(),
        providerRequestId: z.string().min(1).max(500).optional(),
        inputTokens: z.number().int().nonnegative().optional(),
        outputTokens: z.number().int().nonnegative().optional(),
        actualCostMicros: z.number().int().nonnegative().optional(),
        costEvidenceSource: bookGenerationCostEvidenceSourceSchema.optional(),
        pricingVersion: z.string().min(1).max(100).optional(),
        creativeSessionBindings: z
            .array(familyCreativeGenerationBindingSchema)
            .max(100)
            .optional(),
        creationBriefBinding: studioCreationBriefGenerationBindingV1Schema.optional(),
        createdAt: z.string().datetime(),
    })
        .strict())
        .max(100),
})
    .strict();
export const generateStudioContentCommandSchema = z
    .object({
    version: z.literal(1),
    contentItemId: z.string().min(1).max(160),
    action: studioContentProductionActionSchema,
    expectedBookRevision: z.number().int().nonnegative(),
    /**
     * A guided memoir's step: when it ends, the next steps start on the server, so the family
     * can close the page (2026-10-18 audit, G7).
     */
    continueAutomatically: z.literal(true).optional(),
})
    .strict();
export const updateRecipeCommandSchema = z
    .object({
    expectedRevision: z.number().int().nonnegative(),
    recipe: recipeContentV1Schema.omit({ revision: true }),
})
    .strict();
/**
 * A Family Cookbook in its first look keeps at least this many recipes: it is laid out from six
 * approved ones. Every cookbook started before 2 November 2026 keeps this for life.
 */
export const COOKBOOK_MINIMUM_RECIPES = 6;
/**
 * The Family Cookbook's looks (book quality batch, owner decision 2, 2 November 2026): a book
 * keeps the look it started with for life, as a comic does. A cookbook made before then has no
 * stored look and keeps the first; one made from then on stores the second when it is made.
 */
export const COOKBOOK_LOOK_1_ID = "cookbook-family-kitchen-v1";
export const COOKBOOK_LOOK_2_ID = "cookbook-family-kitchen-v2";
/**
 * A cookbook in the second look keeps at least this many recipes (owner decision 4, 2 November
 * 2026): eight recipes and their pictures fill its 28 pages with only a few to spare.
 */
export const COOKBOOK_LOOK_2_MINIMUM_RECIPES = 8;
/**
 * The most recipes a Family Cookbook holds (Stage 2, owner decision 3: start from the twelve
 * questions, go down to six, add your own up to a maximum checked against print cost).
 *
 * Print is not the limit: measured on the cookbook layout on 2026-11-02, 24 recipes lay out in 60
 * pages (84 when every recipe is very long), inside the 200-page photobook, and an 84-page 8×11
 * hardcover is $32.40 with standard shipping, well inside the $75 print cap.
 *
 * The $5 book allowance is the limit (services/src/book-variants/registry.ts, review P2-20). Each
 * recipe is tidied (at most $0.60 held, a few cents settled) and painted (at most $0.51 held, about
 * $0.14 settled with its checks and repaints); a picture starts only while $0.51 is free. Counting
 * $0.06 a tidy, $0.17 a picture, the cover's picture, card readings and some recipes tidied twice
 * after edits (about $0.245 a recipe and $0.08 besides), the last picture of N recipes starts only
 * while 5.00 − (0.245 × N + 0.08) ≥ 0.51: N ≤ 18. (24 recipes would run about $0.96 short.)
 */
export const COOKBOOK_MAXIMUM_RECIPES = 18;
/** Adds a recipe of the family's own to the end of a cookbook. */
export const addBookRecipeCommandV1Schema = z.object({ version: z.literal(1) }).strict();
/** Takes a recipe out of a cookbook, exactly as it was last seen. */
export const removeBookRecipeCommandV1Schema = z
    .object({ expectedRevision: z.number().int().nonnegative() })
    .strict();
/** The order a cookbook's recipes are told and printed in: every recipe, each once. */
export const reorderBookRecipesCommandV1Schema = z
    .object({
    recipeIds: z.array(z.string().min(1).max(160)).min(1).max(COOKBOOK_MAXIMUM_RECIPES),
    expectedOrderRevision: z.number().int().nonnegative(),
})
    .strict()
    .refine(({ recipeIds }) => new Set(recipeIds).size === recipeIds.length, {
    message: "Each recipe appears once.",
    path: ["recipeIds"],
});
export const approveRecipeCommandSchema = z
    .object({
    expectedRevision: z.number().int().positive(),
    expectedBookRevision: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=studio.js.map